import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { ORG_SCHEMA } from "../data/seo";
import { api } from "../lib/api";
import { CONTACT } from "../data/site";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your message has been sent. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32">
      <SEO
        title="Contact Us | Christian Counseling Wesley Chapel, FL"
        description="Reach Beyond Blessed Consulting & Counseling in Wesley Chapel, FL. Call 813-330-0423 or send a message to book a free 15-minute consultation."
        path="/contact"
        jsonLd={ORG_SCHEMA}
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="uppercase tracking-[0.3em] text-xs text-teal mb-4">Contact</p>
          <h1 className="font-serif text-5xl md:text-6xl text-navy leading-none mb-8">Let's begin your<br /> healing journey.</h1>
          <form onSubmit={submit} className="space-y-8 mt-10" data-testid="contact-form">
            <input className="intake-input" placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="contact-name" />
            <input className="intake-input" placeholder="Email address *" type="email" value={form.email} onChange={set("email")} data-testid="contact-email" />
            <input className="intake-input" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
            <input className="intake-input" placeholder="Subject" value={form.subject} onChange={set("subject")} data-testid="contact-subject" />
            <textarea className="intake-input resize-none" rows={4} placeholder="How can we support you? *" value={form.message} onChange={set("message")} data-testid="contact-message" />
            <button type="submit" disabled={loading} data-testid="contact-submit" className="bg-navy text-cream px-8 py-4 rounded-full hover:bg-teal transition-colors inline-flex items-center gap-2 disabled:opacity-60">
              {loading ? <><Loader2 size={17} className="animate-spin" /> Sending...</> : "Send message"}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="bg-navy text-cream p-10 md:p-12 h-full">
            <h2 className="font-serif text-3xl mb-8 text-goldbright">Reach us directly</h2>
            <ul className="space-y-7 text-cream/85">
              <li className="flex items-start gap-4"><Phone className="text-goldbright mt-1" size={20} /><div><p className="text-cream/50 text-xs uppercase tracking-widest mb-1">Phone</p><a href={CONTACT.phoneHref} className="text-lg hover:text-goldbright">{CONTACT.phone}</a></div></li>
              <li className="flex items-start gap-4"><Mail className="text-goldbright mt-1" size={20} /><div><p className="text-cream/50 text-xs uppercase tracking-widest mb-1">Email</p><a href={`mailto:${CONTACT.email}`} className="text-lg hover:text-goldbright">{CONTACT.email}</a></div></li>
              <li className="flex items-start gap-4"><MapPin className="text-goldbright mt-1" size={20} /><div><p className="text-cream/50 text-xs uppercase tracking-widest mb-1">Office</p><p className="text-lg">{CONTACT.address}</p></div></li>
            </ul>
            <div className="mt-10 aspect-video overflow-hidden">
              <iframe title="office-map" width="100%" height="100%" style={{ border: 0, filter: "grayscale(0.2)" }} loading="lazy"
                src="https://maps.google.com/maps?q=30740%20State%20Road%2054%20Wesley%20Chapel%20FL%2033543&t=&z=15&ie=UTF8&iwloc=&output=embed" />
            </div>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <a href={CONTACT.request} target="_blank" rel="noreferrer" data-testid="contact-request" className="bg-goldbright text-navy px-6 py-3 rounded-full text-center hover:bg-cream transition-colors">Request Appointment</a>
              <a href={CONTACT.portal} target="_blank" rel="noreferrer" data-testid="contact-portal" className="border border-cream/30 px-6 py-3 rounded-full text-center hover:bg-cream/10 transition-colors">Client Portal</a>
              <a href={CONTACT.growTherapy} target="_blank" rel="noreferrer" data-testid="contact-grow-therapy" className="sm:col-span-2 bg-teal text-cream px-6 py-3 rounded-full text-center hover:bg-cream hover:text-teal transition-colors border border-teal">
                Grow Therapy clients — book / log in
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
